import { useState, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { ProductCard } from '@/components/ProductCard';
import { HeroInfoBox } from '@/components/HeroInfoBox';
import { useCart } from '@/context/CartContext';
import { useSettings } from '@/context/SettingsContext';
import api from '@/lib/api';
import { Search, ArrowRight, ShoppingBag, Star, Shield, Clock, X } from 'lucide-react';

const HERO_IMG = "https://images.unsplash.com/photo-1767730794056-07ca03baf8b5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwyfHxzeWx0JTIwYmVhY2glMjBkdW5lcyUyMGxhbmRzY2FwZXxlbnwwfHx8fDE3NzM1MDQ5ODZ8MA&ixlib=rb-4.1.0&q=85";

// German umlaut normalization for sorting
const normalizeGerman = (text) => {
  if (!text) return "";
  const replacements = [['ä', 'ae'], ['Ä', 'Ae'], ['ö', 'oe'], ['Ö', 'Oe'], ['ü', 'ue'], ['Ü', 'Ue'], ['ß', 'ss']];
  let result = text.toLowerCase();
  replacements.forEach(([old, newChar]) => { result = result.split(old).join(newChar); });
  return result;
};

export default function HomePage() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const { settings: s, settingsLoaded } = useSettings();
  const [search, setSearch] = useState('');
  const [selectedCategories, setSelectedCategories] = useState([]);  // Mehrfachauswahl
  const [defaultCategoryApplied, setDefaultCategoryApplied] = useState(false);  // Track if default was applied
  const [sort, setSort] = useState('sort_order');
  const { totalItems, setIsOpen } = useCart();
  const [shopSettings, setShopSettings] = useState(null);

  useEffect(() => {
    api.get('/products/public').then(r => setProducts(r.data)).catch(() => {});
    api.get('/categories/public').then(r => {
      // Sortiere alphabetisch mit deutscher Umlaut-Behandlung
      const sorted = [...r.data].sort((a, b) => 
        normalizeGerman(a.name || '').localeCompare(normalizeGerman(b.name || ''))
      );
      setCategories(sorted);
    }).catch(() => {});
    // Fetch shop settings for default category
    api.get('/shop-settings').then(r => setShopSettings(r.data)).catch(() => {});
  }, []);

  // Apply default category once when both categories and shop settings are loaded
  useEffect(() => {
    if (!defaultCategoryApplied && categories.length > 0 && shopSettings) {
      const defaultCatId = shopSettings.default_category_id;
      if (defaultCatId) {
        // Check if the default category exists and is active
        const categoryExists = categories.some(c => c.id === defaultCatId);
        if (categoryExists) {
          setSelectedCategories([defaultCatId]);
        }
      }
      setDefaultCategoryApplied(true);
    }
  }, [categories, shopSettings, defaultCategoryApplied]);

  // Toggle category selection
  const toggleCategory = (categoryId) => {
    setSelectedCategories(prev => 
      prev.includes(categoryId) 
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  // Clear all category filters
  const clearCategoryFilters = () => {
    setSelectedCategories([]);
  };

  // Check if a category is "Vegan" (for special description-based filtering)
  const isVeganCategory = (categoryId) => {
    const cat = categories.find(c => c.id === categoryId);
    return cat && cat.name && cat.name.toLowerCase() === 'vegan';
  };

  const filteredProducts = products.filter(p => {
    const matchSearch = !search || p.name.toLowerCase().includes(search.toLowerCase()) || p.article_number.toLowerCase().includes(search.toLowerCase());
    
    // Category matching with special Vegan logic
    let matchCat = selectedCategories.length === 0;
    if (!matchCat) {
      // Check direct category assignment
      matchCat = selectedCategories.includes(p.category_id);
      
      // Special case: If "Vegan" category is selected, also match products with "vegan" in description
      if (!matchCat && selectedCategories.some(isVeganCategory)) {
        const description = (p.description || '').toLowerCase();
        if (description.includes('vegan')) {
          matchCat = true;
        }
      }
    }
    
    return matchSearch && matchCat;
  }).sort((a, b) => {
    if (sort === 'price_asc') return a.price - b.price;
    if (sort === 'price_desc') return b.price - a.price;
    if (sort === 'name') return a.name.localeCompare(b.name, 'de');
    if (sort === 'popular') return (b.auto_order_count || 0) - (a.auto_order_count || 0);
    // Standard: sort_order > 0 zuerst (aufsteigend), dann sort_order = 0 alphabetisch
    const aOrder = a.sort_order || 0;
    const bOrder = b.sort_order || 0;
    if (aOrder > 0 && bOrder > 0) {
      if (aOrder !== bOrder) return aOrder - bOrder;
      return a.name.localeCompare(b.name, 'de');
    }
    if (aOrder > 0 && bOrder === 0) return -1;
    if (aOrder === 0 && bOrder > 0) return 1;
    return a.name.localeCompare(b.name, 'de');
  });

  const faqEntries = [1,2,3,4].map(i => ({
    question: s[`faq_${i}_question`] || '',
    answer: s[`faq_${i}_answer`] || '',
  })).filter(f => f.question);

  if (!settingsLoaded) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-2 border-[#0E518D] border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="pt-20">
      {/* Hero */}
      <section className="bg-white" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center py-12 md:py-20 lg:py-24">
            <div className="order-2 lg:order-1">
              <div className="inline-flex items-center gap-2 bg-[#0E518D]/8 text-[#0E518D] px-4 py-2 rounded-full text-sm font-medium mb-6 opacity-0 animate-fade-in-up">
                <Star className="w-4 h-4" />
                {s.hero_badge || ''}
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#0E518D] leading-tight mb-5 tracking-tight opacity-0 animate-fade-in-up animate-delay-100" data-testid="hero-heading">
                {s.hero_title || ''}
              </h1>
              <p className="text-base md:text-lg text-[#475569] leading-relaxed mb-8 max-w-xl opacity-0 animate-fade-in-up animate-delay-200" data-testid="hero-subtitle">
                {s.hero_subtitle || ''}
              </p>
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 opacity-0 animate-fade-in-up animate-delay-300">
                <Button onClick={() => document.getElementById('produkte')?.scrollIntoView({ behavior: 'smooth' })}
                  className="bg-[#0E518D] hover:bg-[#0B4171] text-white rounded-sm px-8 py-4 font-medium tracking-wide text-sm uppercase shadow-md hover:shadow-lg transition-all" data-testid="hero-cta-button">
                  {s.hero_cta_text || ''}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
                
                {/* Hero Info Box - rechts neben dem Button */}
                <HeroInfoBox 
                  title={s.hero_infobox_title || ''} 
                  text={s.hero_infobox_text || ''} 
                />
                
                {totalItems > 0 && (
                  <Button variant="outline" onClick={() => setIsOpen(true)} className="border-[#0E518D] text-[#0E518D] rounded-sm px-6 py-4 uppercase tracking-wide text-sm" data-testid="hero-cart-button">
                    <ShoppingBag className="w-4 h-4 mr-2" />
                    Warenkorb ({totalItems})
                  </Button>
                )}
              </div>
            </div>
            <div className="order-1 lg:order-2 opacity-0 animate-fade-in-up animate-delay-200">
              <div className="relative">
                <div className="rounded-sm overflow-hidden shadow-xl">
                  <img src={s.hero_image || HERO_IMG} alt="GB Sylt Bestellwelt" className="w-full h-64 sm:h-80 lg:h-[420px] object-cover" data-testid="hero-image" />
                </div>
                <div className="absolute -bottom-4 -left-4 w-24 h-24 bg-[#0E518D]/10 rounded-sm -z-10" />
                <div className="absolute -top-4 -right-4 w-16 h-16 bg-[#D4AF37]/10 rounded-sm -z-10" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Banner */}
      <section className="bg-[#0E518D] py-3" data-testid="trust-banner">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center gap-6 flex-wrap">
            <div className="flex items-center gap-2 text-white/90 text-sm">
              <Shield className="w-4 h-4 text-[#D4AF37]" />
              <span>{s.trust_1 || ''}</span>
            </div>
            <div className="flex items-center gap-2 text-white/90 text-sm">
              <Clock className="w-4 h-4 text-[#D4AF37]" />
              <span>{s.trust_2 || ''}</span>
            </div>
            <div className="flex items-center gap-2 text-white/90 text-sm">
              <Star className="w-4 h-4 text-[#D4AF37]" />
              <span>{s.trust_3 || ''}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Products */}
      <section id="produkte" className="py-16 md:py-24 bg-[#F7F9FC]" data-testid="products-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-[#0E518D] mb-4">{s.products_title || ''}</h2>
            <p className="text-base text-[#475569] max-w-2xl mx-auto">{s.products_subtitle || ''}</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-4 mb-8" data-testid="product-filters">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input placeholder={s.products_search_placeholder || ''} value={search} onChange={e => setSearch(e.target.value)} className="pl-10 rounded-sm border-slate-300 bg-white" data-testid="product-search" />
            </div>
            <select
              value={sort}
              onChange={e => setSort(e.target.value)}
              className="w-full sm:w-48 rounded-sm border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0E518D] focus:border-transparent"
              data-testid="sort-filter"
            >
              <option value="sort_order">Standard</option>
              <option value="price_asc">Preis aufsteigend</option>
              <option value="price_desc">Preis absteigend</option>
              <option value="name">Name A-Z</option>
              <option value="popular">Beliebtheit</option>
            </select>
          </div>
          
          {/* Kategorie Mehrfachauswahl */}
          <div className="mb-8" data-testid="category-multi-filter">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-medium text-slate-700">Kategorien filtern:</span>
              {selectedCategories.length > 0 && (
                <button
                  onClick={clearCategoryFilters}
                  className="text-xs text-[#0E518D] hover:text-[#0B4171] flex items-center gap-1"
                  data-testid="clear-category-filter"
                >
                  <X className="h-3 w-3" />
                  Filter zurücksetzen ({selectedCategories.length})
                </button>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              {categories.map(c => (
                <label
                  key={c.id}
                  className={`inline-flex items-center gap-2 px-3 py-2 rounded-sm border cursor-pointer transition-colors text-sm
                    ${selectedCategories.includes(c.id)
                      ? 'bg-[#0E518D] text-white border-[#0E518D]'
                      : 'bg-white text-slate-700 border-slate-300 hover:border-[#0E518D] hover:bg-slate-50'
                    }`}
                  data-testid={`category-checkbox-${c.id}`}
                >
                  <Checkbox
                    checked={selectedCategories.includes(c.id)}
                    onCheckedChange={() => toggleCategory(c.id)}
                    className={`${selectedCategories.includes(c.id) ? 'border-white data-[state=checked]:bg-white data-[state=checked]:text-[#0E518D]' : 'border-slate-400'}`}
                  />
                  {c.name}
                </label>
              ))}
            </div>
            {selectedCategories.length > 0 && (
              <p className="text-xs text-slate-500 mt-2">
                {filteredProducts.length} Produkt{filteredProducts.length !== 1 ? 'e' : ''} in {selectedCategories.length} ausgewählte{selectedCategories.length !== 1 ? 'n' : 'r'} Kategorie{selectedCategories.length !== 1 ? 'n' : ''}
              </p>
            )}
          </div>
          {filteredProducts.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-testid="product-grid">
              {filteredProducts.map(p => <ProductCard key={p.id} product={p} />)}
            </div>
          ) : (
            <div className="text-center py-16" data-testid="no-products">
              <ShoppingBag className="h-16 w-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-500 mb-2">
                {products.length === 0 ? (s.products_empty || '') : (s.products_no_results || '')}
              </h3>
              <p className="text-sm text-slate-400">
                {products.length === 0 ? (s.products_empty_sub || '') : (s.products_no_results_sub || '')}
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Intro Text */}
      {s.intro_text && (
        <section className="py-16 md:py-24 bg-white" data-testid="info-section">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="prose prose-slate mx-auto" dangerouslySetInnerHTML={{ __html: s.intro_text }} />
          </div>
        </section>
      )}

      {/* Steps */}
      <section className="py-16 md:py-24 bg-white" data-testid="steps-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-[#0E518D] text-center mb-14">{s.steps_title || ''}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-16">
            {[
              { num: '01', title: s.step_1_title || '', desc: s.step_1_desc || '', icon: ShoppingBag },
              { num: '02', title: s.step_2_title || '', desc: s.step_2_desc || '', icon: ArrowRight },
              { num: '03', title: s.step_3_title || '', desc: s.step_3_desc || '', icon: Star },
            ].map((step, i) => (
              <div key={i} className="relative text-center">
                <div className="relative inline-flex items-center justify-center mb-6">
                  <div className="w-16 h-16 bg-[#0E518D] rounded-full flex items-center justify-center">
                    <step.icon className="w-7 h-7 text-white" />
                  </div>
                  <span className="absolute -top-2 -right-2 w-7 h-7 bg-[#D4AF37] rounded-full text-white text-xs font-bold flex items-center justify-center shadow-sm">{step.num}</span>
                </div>
                <h3 className="text-xl font-semibold text-[#1E293B] mb-3">{step.title}</h3>
                <p className="text-sm text-[#475569] leading-relaxed max-w-xs mx-auto">{step.desc}</p>
                {i < 2 && <div className="hidden md:block absolute top-8 left-[calc(50%+48px)] w-[calc(100%-96px)]"><div className="border-t-2 border-dashed border-[#0E518D]/15" /></div>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      {faqEntries.length > 0 && (
        <section id="faq" className="py-16 md:py-24 bg-[#F7F9FC]" data-testid="faq-section">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-[#0E518D] text-center mb-12" data-testid="faq-title">{s.faq_title || ''}</h2>
            <Accordion type="single" collapsible className="space-y-3">
              {faqEntries.map((faq, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="bg-white border border-slate-200 rounded-sm px-6 shadow-sm" data-testid={`faq-item-${i}`}>
                  <AccordionTrigger className="text-sm font-medium text-[#1E293B] hover:no-underline py-5">{faq.question}</AccordionTrigger>
                  <AccordionContent className="text-sm text-[#475569] pb-5 leading-relaxed">{faq.answer}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-16 md:py-24 bg-white" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="bg-[#0E518D] rounded-sm p-10 md:p-14 shadow-lg">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">{s.cta_title || ''}</h2>
            <p className="text-base text-white/80 leading-relaxed mb-6 max-w-2xl mx-auto">{s.cta_text || ''}</p>
            <Button onClick={() => document.getElementById('produkte')?.scrollIntoView({ behavior: 'smooth' })}
              className="bg-white hover:bg-slate-50 text-[#0E518D] rounded-sm px-10 py-4 font-semibold tracking-wide text-sm uppercase shadow-md hover:shadow-lg transition-all" data-testid="cta-button">
              {s.cta_button || ''}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
